import Phaser from "phaser";
import type { EconomyInvestmentSummary } from "../../../../shared/types/BattleEvent";
import type { LaneNumber } from "../../../../shared/types/CommanderDecision";
import mapConfig from "../data/mapConfig.json";

type EconomyInvestment = {
  lane: LaneNumber;
  x: number;
  y: number;
  active: boolean;
  builtAt: number;
  expiresAt: number;
  marker: Phaser.GameObjects.Container;
  ring: Phaser.GameObjects.Arc;
  label: Phaser.GameObjects.Text;
  timer: Phaser.GameObjects.Text;
  fill: Phaser.GameObjects.Rectangle;
};

export type EconomyInvestmentCallbacks = {
  onBuilt?: (investment: EconomyInvestmentSummary) => void;
  onDisabled?: (investment: EconomyInvestmentSummary) => void;
  onExpired?: (investment: EconomyInvestmentSummary) => void;
};

const DURATION_MS = 60000;
const INCOME_PER_SECOND = 0.55;

export class EconomyInvestmentSystem {
  private readonly investments = new Map<LaneNumber, EconomyInvestment>();

  constructor(private readonly scene: Phaser.Scene, private readonly callbacks: EconomyInvestmentCallbacks = {}) {}

  canBuild(lane: LaneNumber) {
    return !this.investments.has(lane);
  }

  build(lane: LaneNumber, time: number) {
    if (!this.canBuild(lane)) return undefined;
    const laneConfig = mapConfig.lanes.find((item) => item.lane === lane) ?? mapConfig.lanes[2]!;
    const x = this.investmentX(lane);
    const marker = this.createMarker(x, laneConfig.y);
    const investment: EconomyInvestment = {
      lane,
      x,
      y: laneConfig.y,
      active: true,
      builtAt: time,
      expiresAt: time + DURATION_MS,
      marker: marker.container,
      ring: marker.ring,
      label: marker.label,
      timer: marker.timer,
      fill: marker.fill
    };
    this.investments.set(lane, investment);
    this.callbacks.onBuilt?.(this.summaryFor(investment, time));
    return investment;
  }

  update(time: number) {
    for (const [lane, investment] of [...this.investments.entries()]) {
      if (time >= investment.expiresAt) {
        this.callbacks.onExpired?.(this.summaryFor(investment, time));
        investment.marker.destroy();
        this.investments.delete(lane);
        continue;
      }
      this.updateMarker(investment, time);
    }
  }

  disableLane(lane: LaneNumber, time: number) {
    const investment = this.investments.get(lane);
    if (!investment || !investment.active) return;
    investment.active = false;
    investment.ring.setStrokeStyle(2, 0x7a7f86, 0.72);
    investment.label.setText("X").setColor("#aeb4bc");
    investment.fill.setFillStyle(0x7a7f86, 0.72);
    this.callbacks.onDisabled?.(this.summaryFor(investment, time));
  }

  get activeCount() {
    return [...this.investments.values()].filter((investment) => investment.active).length;
  }

  get incomePerSecond() {
    return Number((this.activeCount * INCOME_PER_SECOND).toFixed(2));
  }

  summaries(time: number): EconomyInvestmentSummary[] {
    return [...this.investments.values()].map((investment) => this.summaryFor(investment, time));
  }

  private investmentX(lane: LaneNumber) {
    if (lane === 1) return 1005;
    if (lane === 2) return 1010;
    if (lane === 3) return 1080;
    if (lane === 4) return 1035;
    return 1015;
  }

  private createMarker(x: number, y: number) {
    const container = this.scene.add.container(x, y - 38).setDepth(52);
    const base = this.scene.add.ellipse(0, 23, 58, 18, 0x030305, 0.36);
    const ring = this.scene.add.circle(0, 0, 23).setStrokeStyle(2, 0xffe58e, 0.9);
    const label = this.scene.add
      .text(0, -8, "G", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "18px",
        color: "#ffe58e",
        stroke: "#111016",
        strokeThickness: 3
      })
      .setResolution(2)
      .setOrigin(0.5);
    const bar = this.scene.add.rectangle(0, 18, 44, 5, 0x07080a, 0.82);
    const fill = this.scene.add.rectangle(-22, 18, 44, 4, 0xffe58e, 0.94).setOrigin(0, 0.5);
    const timer = this.scene.add
      .text(0, 27, "60s", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "11px",
        color: "#fff7d6",
        stroke: "#111016",
        strokeThickness: 2
      })
      .setResolution(2)
      .setOrigin(0.5);
    container.add([base, ring, label, bar, fill, timer]);
    return { container, ring, label, fill, timer };
  }

  private updateMarker(investment: EconomyInvestment, time: number) {
    const remaining = Math.max(0, investment.expiresAt - time);
    const progress = Phaser.Math.Clamp(remaining / DURATION_MS, 0, 1);
    investment.fill.width = Math.max(1, 44 * progress);
    investment.timer.setText(`${Math.ceil(remaining / 1000)}s`);
    investment.marker.setAlpha(investment.active ? 1 : 0.62);
  }

  private summaryFor(investment: EconomyInvestment, time: number): EconomyInvestmentSummary {
    return {
      lane: investment.lane,
      active: investment.active,
      remainingSec: Math.max(0, Math.ceil((investment.expiresAt - time) / 1000)),
      incomePerSecond: investment.active ? INCOME_PER_SECOND : 0
    };
  }
}
